import * as React from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import { excludeKeys, useChooseRef } from '../../utils';
import { IButtonProps, ButtonSize } from './button-types';
import { Callout } from '../callout';
import { Icon } from '../icon';
import { Menu, IFlyoutMenuProps } from '../menu';
import { selectThemeSemantics } from '../../styling';

const StyledIcon = styled(Icon)`
    transition: fill .15s ease-in-out;
`;

const buttonSize = { small: '30px', medium: '40px', large: '50px' };

function getMarginBasedOnSize(size: ButtonSize, iconExists: boolean, iconPositionAtRight: boolean, theme: DefaultTheme) {
    const { semantics: { gap } } = theme;
    switch (size) {
        case 'small':
            if (iconExists) {
                return iconPositionAtRight ? `0px 0px 0px ${gap.xxl}` : `0px ${gap.xxl} 0px 0px`;
            }
            return `${gap.none} ${gap.xxl}`;
        case 'medium':
            if (iconExists) {
                return iconPositionAtRight ? `0px 0px 0px ${gap.xxxl}` : `0px ${gap.xxxl} 0px 0px`;
            }
            return `${gap.none} ${gap.xxxl}`;
        case 'large':
            if (iconExists) {
                return iconPositionAtRight ? `0px 0px 0px ${gap.xxxxl}` : `0px ${gap.xxxxl} 0px 0px`;
            }
            return `${gap.none} ${gap.xxxxl}`;
        default:
            return undefined;
    }
}

const MenuIcon = styled(Icon).attrs({
    iconName: 'ap-right-arrow-thin2.outline',
    iconSize: '16'
}) <{ $iconPositionAtRight: boolean; }>`
    transition: fill .15s ease-in-out;
    transform: rotate(90deg);
    margin: ${({ theme: { semantics: { gap } }, $iconPositionAtRight }) =>
        ($iconPositionAtRight ? `0px ${gap.xl} 0px 0px` : `0px 0px 0px ${gap.xl}`)};
`;

const Label = styled.span.attrs({
    className: 'ap-button-label'
}) <{ $size?: ButtonSize; $iconExists: boolean; $iconPositionAtRight: boolean; }>`
    color: inherit;
    font: inherit;
    text-overflow: ellipsis;
    white-space: nowrap;
    width : 100%;
    overflow: hidden;
    margin: ${({ theme, $size, $iconPositionAtRight, $iconExists }) => getMarginBasedOnSize($size!, $iconExists, $iconPositionAtRight, theme)};
`;

const ContextMenu = styled(Menu.FlyoutMenu)`
    border: none;
`;

const ButtonBase = React.memo((props: IButtonProps) => {
    const { label, elementRef, contextMenuProps, onClick, iconName, iconSize, isBeakVisibleOnCallout, hidden, iconPosition, title, size = 'medium', hideMenuIcon } = props;
    const [isCalloutHidden, toggleCallout] = React.useState(true);
    const buttonRef = useChooseRef(elementRef) as React.RefObject<HTMLButtonElement>;
    const buttonProps = React.useMemo(() =>
        excludeKeys(props, 'label', 'children', 'elementRef', 'contextMenuProps', 'iconName', 'iconSize', 'isBeakVisibleOnCallout', 'iconPosition', 'outline', 'hideMenuIcon'), [props]);

    const _onDismiss = React.useCallback(() => {
        toggleCallout(true);
    }, [toggleCallout]);

    const _onButtonClick = React.useCallback((ev: React.MouseEvent<HTMLButtonElement>) => {
        if (contextMenuProps !== undefined) {
            toggleCallout(!isCalloutHidden);
        }
        if (onClick !== undefined) {
            onClick(ev);
        }
    }, [onClick, contextMenuProps, isCalloutHidden]);

    const _onContextMenuChangeChange = contextMenuProps?.onChange;
    const _onMenuSelected = React.useCallback((selectedItenId: string) => {
        toggleCallout(true);
        if (_onContextMenuChangeChange) {
            _onContextMenuChangeChange(selectedItenId);
        }
    }, [_onContextMenuChangeChange, toggleCallout]);

    const menuProps: IFlyoutMenuProps = React.useMemo(() => ({
        ...contextMenuProps!,
        onChange: _onMenuSelected
    }), [contextMenuProps, _onMenuSelected]);

    const iconPositionAtRight = iconPosition === 'right';

    return hidden ? null : (
        <React.Fragment>
            <button
                {...buttonProps}
                ref={buttonRef}
                title={title ? title : label}                
                onClick={_onButtonClick}>
                {iconName && <StyledIcon iconName={iconName} iconSize={iconSize} />}
                {contextMenuProps && !hideMenuIcon && <MenuIcon $iconPositionAtRight={iconPositionAtRight} />}
                {label && <Label $size={size} $iconExists={!!iconName} $iconPositionAtRight={iconPositionAtRight}>{label}</Label>}
            </button>
            {
                contextMenuProps ? (
                    <StyledCallout targetRef={buttonRef} hidden={isCalloutHidden} isBeakVisible={isBeakVisibleOnCallout} onDismiss={_onDismiss} autofocusOnOpen closeOnEscape>
                        {React.createElement(ContextMenu, menuProps)}
                    </StyledCallout>
                ) : null
            }
        </React.Fragment>
    );
});

ButtonBase.displayName = 'Button';

const iconOnlyStyle = css`
    margin: ${selectThemeSemantics(({ gap }) => `${gap.none} ${gap.l}`)};
`;

const iconWithOtherContentStyle = css<IButtonProps>`
    margin: ${({ theme: { semantics: { gap } }, iconPosition, size = 'medium' }) => {
        const iconPositionAtRight = iconPosition === 'right';
        switch (size) {
            case 'small':
                return (iconPositionAtRight ? `0px ${gap.xxl} 0px ${gap.l}` : `0px ${gap.l} 0px ${gap.xxl}`);
            case 'medium':
                return (iconPositionAtRight ? `0px ${gap.xxxl} 0px ${gap.l}` : `0px ${gap.l} 0px ${gap.xxxl}`);
            case 'large':
                return (iconPositionAtRight ? `0px ${gap.xxxxl} 0px ${gap.l}` : `0px ${gap.l} 0px ${gap.xxxxl}`);
            default:
                return undefined;
        }
    }};`;

export const StyledCallout = styled(Callout)`
    z-index: 4;
`;

export const Button = styled(ButtonBase).attrs(() => ({
    className: String('ap-button')
}))`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-direction: ${({ iconPosition }) => (iconPosition === 'right' ? 'row-reverse' : 'row')};
    border-radius: ${({ theme: { button: { composites } } }) => composites.borderRadius};
    border-width: ${({ theme: { button: { composites } } }) => composites.borderWidth};
    border-style: solid;
    box-sizing: border-box;
    height: ${({ size = 'medium' }) => buttonSize[size]};
    font-family: inherit;
    font-size: ${({ theme: { typography: { medium, small } }, size }) => (size === 'small' ? small.fontSize : medium.fontSize)};
    font-weight: ${({ theme: { typography: { medium, small } }, size }) => (size === 'small' ? small.fontWeight : medium.fontWeight)};
    cursor: pointer;
    transition: all .15s ease-in-out;
    padding: ${selectThemeSemantics(({ gap }) => gap.none)};

    ${StyledIcon} {
        ${({ label, contextMenuProps }) => (!(label || contextMenuProps) ? iconOnlyStyle : iconWithOtherContentStyle)}
    }
`;
